import { LinearProgress } from "@mui/material";
import { FullScreenDialog } from "common/components";
import { useTranslation } from "common/locales";
import { AnalyticsEvents, analytics } from "common/analytics";
import { useFormik } from "formik";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clearSessionState } from "services/auth";
import { completePasswordReset, initializeApp, initializeFarm } from "services";
import { useAppStore } from "stores/useAppStore";
import PasswordResetUserDetails from "./PasswordResetUserDetails";
import { useNotification } from "./NotificationProvider";
import { markResetTokenCompleted } from "./deepLinkTarget";

export interface FormValues {
  password: string;
  confirmPassword: string;
}

export interface FormErrors {
  confirmPassword?: string;
}

export default function PasswordResetDialog() {
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const [passwordResetError, setPasswordResetError] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);
  const [passwordResetInProgress, setPasswordResetInProgress] = useState(false);
  const token =
    new URLSearchParams(location.search).get("resetToken") ||
    new URLSearchParams(window.location.search).get("resetToken");

  const formik = useFormik<FormValues>({
    initialValues: { password: "", confirmPassword: "" },
    onSubmit: () => void handlePasswordReset(),
    validate: (values) => {
      setPasswordResetError(false);
      return values.password !== values.confirmPassword
        ? { confirmPassword: t("signup.confirmPassword.error") }
        : {};
    },
  });

  const handlePasswordReset = async () => {
    if (!token || !formik.values.password) return;
    setPasswordResetInProgress(true);
    try {
      const session = await completePasswordReset(
        token,
        formik.values.password
      );
      markResetTokenCompleted(token);
      // A different account may already be signed in on this device.
      clearSessionState();
      localStorage.setItem("jwt", session.token);
      const result = await initializeApp();
      const isCorporate = !!result.user?.corporations?.length;
      const defaultFarm = result.farms?.[0];
      analytics.track(AnalyticsEvents.PASSWORD_RESET_COMPLETED);
      notify(t("passwordChange.success"), "success");
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("resetToken");
      window.history.replaceState(window.history.state, "", currentUrl);
      navigate("/", { replace: true });
      // Farm loading can be slow on mobile. The reset succeeded and the session
      // is ready, so let the app show its loading screen after this dialog closes.
      if (defaultFarm && !isCorporate) {
        void initializeFarm(defaultFarm).catch((error: unknown) => {
          console.error(
            "Failed to initialize farm after password reset",
            error
          );
          useAppStore.getState().setLoading("idle");
        });
      }
    } catch (error) {
      console.error(error);
      setPasswordResetError(true);
      const linkInvalid =
        error instanceof Error && "status" in error && error.status === 400;
      setInvalidToken(linkInvalid);
      notify(
        t(linkInvalid ? "passwordReset.linkInvalid" : "passwordChange.failure"),
        "error"
      );
      analytics.track(AnalyticsEvents.PASSWORD_RESET_FAILED);
    } finally {
      setPasswordResetInProgress(false);
    }
  };

  return (
    <FullScreenDialog>
      {passwordResetInProgress && <LinearProgress />}
      <PasswordResetUserDetails
        values={formik.values}
        errors={formik.errors}
        handleChange={formik.handleChange}
        handleSubmit={formik.handleSubmit}
        passwordUpdateError={passwordResetError}
        passwordResetInProgress={passwordResetInProgress}
        hasResetToken={!!token && !invalidToken}
      />
    </FullScreenDialog>
  );
}
